import React, { useEffect, useState, useMemo } from 'react';
import { Table, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { DASHBOARD_QUERY_STUDENT_LEADS } from '../queries';
import Panel from 'common/components/panel';
import TableSearchInput from 'common/components/select-filter/table-search-input';
import { formatDay, dayFormatType } from 'common/helpers/day';
import { sortDirection, sortFields } from '~constants/bookings';
import { getTitle } from './helpers';
import { isEqual } from 'lodash';
import { getBookingDetailUrl } from '~helpers/order';
import { bookingJourneyType, ALL_BOOKING_STAGES, ONGOING_STAGES } from 'common/constants/booking';

const StudentLeads = ({ studentId, ongoing }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [leads, setLeads] = useState([]);
  const [listTotalCount, setListTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const [queryFilter, setQueryFilter] = useState({
    referenceId: null,
    propertyName: null,
    stages: ongoing ? ONGOING_STAGES : null,
    bookingJourneys: null,
    sortDirection: 'DESCENDING',
    sortBy: 'CREATED_AT',
  });

  const { loading, data: studentsLeadsResponse } = useQuery(DASHBOARD_QUERY_STUDENT_LEADS, {
    variables: {
      id: studentId,
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: queryFilter.sortBy,
      sortDirection: queryFilter.sortDirection,
      referenceId: queryFilter.referenceId,
      propertyName: queryFilter.propertyName,
      stages: queryFilter.stages,
      bookingJourneys: queryFilter.bookingJourneys,
    },
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e?.toString());
    },
  });

  useEffect(() => {
    const result = studentsLeadsResponse?.listStudents?.edges?.[0]?.node?.orders?.edges || [];
    setLeads(result.map(item => item.node));
    setListTotalCount(studentsLeadsResponse?.listStudents?.edges?.[0]?.node?.orders?.totalCount);
  }, [studentsLeadsResponse]);

  const handleTableChange = (page, filter, sorter) => {
    const oldDirection = queryFilter.sortDirection;
    const newDirection = sortDirection[sorter?.order];
    const oldPage = pagination.pageNumber;
    const newPage = page.current;

    if (page?.pageSize && page?.current) {
      if (page?.pageSize !== pagination.pageSize) {
        setPagination({
          pageNumber: 1,
          pageSize: page.pageSize,
        });
      } else {
        setPagination({
          pageNumber: page.current,
          pageSize: page.pageSize,
        });
      }
    }

    if (!isEqual(filter?.stages, queryFilter?.stages)) {
      setQueryFilter(prev => ({
        ...prev,
        stages: filter?.stage?.length > 0 ? filter?.stage : null,
      }));
    }

    if (!isEqual(filter?.bookingJourney, queryFilter?.bookingJourneys)) {
      setQueryFilter(prev => ({
        ...prev,
        bookingJourneys: filter?.bookingJourney?.length > 0 ? filter?.bookingJourney : null,
      }));
    }

    if (oldDirection !== newDirection && oldPage === newPage) {
      if (!sorter?.order) {
        setQueryFilter(prev => ({
          ...prev,
          sortBy: sortFields.createdAt,
          sortDirection: sortDirection.descend,
        }));
        setPagination(prev => ({
          ...prev,
          pageNumber: 1,
        }));
      } else {
        setQueryFilter(prev => ({
          ...prev,
          sortBy: sortFields[sorter.field],
          sortDirection: sortDirection[sorter.order],
        }));
        setPagination(prev => ({
          ...prev,
          pageNumber: 1,
        }));
      }
    }
  };

  const statusFilter = useMemo(() => {
    return Object.keys(ALL_BOOKING_STAGES).map(item => ({
      text: t(`cms:cms.students.list.column.stage.${item.toLowerCase()}`) || '-',
      value: item,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bookingJourneyFilter = useMemo(() => {
    return Object.keys(bookingJourneyType)
      .filter(item => item !== bookingJourneyType.MANUAL)
      .map(item => ({
        text: t(`cms:cms.students.list.column.listing_plan.${item.toLowerCase()}`) || '-',
        value: item,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleView = (bookingJourney, referenceId) => {
    history.push(getBookingDetailUrl(bookingJourney, referenceId, 'Students'));
  };

  const columns = [
    {
      title: t('cms:cms.students.detail.table.booking_number'),
      width: 200,
      dataIndex: 'referenceId',
      key: 'referenceId',
      render: (text, record) => record.referenceId ?? '-',
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter?.referenceId ? '#38B2A6' : undefined }} />
      ),
      filterDropdown: ({ confirm }) => (
        <TableSearchInput
          value={queryFilter.referenceId}
          onClick={value => {
            confirm();
            setQueryFilter(prev => ({
              ...prev,
              referenceId: value,
            }));
            setPagination(prev => ({
              ...prev,
              pageNumber: 1,
            }));
          }}
        />
      ),
    },
    {
      title: t('cms:cms.students.detail.table.property_name'),
      dataIndex: 'propertyName',
      key: 'propertyName',
      render: (text, record) => record.propertyName ?? '-',
      filterDropdown: ({ confirm }) => (
        <div className="partner__body__list__columns-dropdown">
          <TableSearchInput
            value={queryFilter.propertyName}
            onClick={value => {
              confirm();
              setQueryFilter(prev => ({
                ...prev,
                propertyName: value,
              }));
              setPagination(prev => ({
                ...prev,
                pageNumber: 1,
              }));
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter?.propertyName ? '#38b2a6' : undefined }} />
      ),
    },
    {
      title: t('cms:cms.students.detail.table.move_in_date'),
      dataIndex: 'moveInDate',
      key: 'moveInDate',
      render: (text, record) => record.moveInDate ?? '-',
    },
    {
      title: t('cms:cms.students.detail.table.move_out_date'),
      dataIndex: 'moveOutDate',
      key: 'moveOutDate',
      render: (text, record) => record.moveOutDate ?? '-',
    },
    {
      title: t('cms:cms.students.detail.table.booking_status'),
      width: 180,
      dataIndex: 'stage',
      key: 'stage',
      filters: statusFilter,
      filteredValue: queryFilter.stages || null,
      render: (text, record) =>
        record.stage ? t(`cms:cms.students.list.column.stage.${record.stage.toLowerCase()}`) : '-',
    },
    {
      title: t('cms:cms.students.detail.table.listing_plan'),
      dataIndex: 'bookingJourney',
      key: 'bookingJourney',
      filters: bookingJourneyFilter,
      render: (text, record) =>
        record.bookingJourney
          ? t(`cms:cms.students.list.column.listing_plan.${record.bookingJourney.toLowerCase()}`)
          : '-',
    },
    {
      title: t('cms:cms.students.detail.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: text => {
        return (
          <span className="students-page__body__list__column-body students-page__body__list__column--min-w-180">
            {formatDay(dayFormatType.UKWITHHM, text) || '-'}
          </span>
        );
      },
    },
    {
      title: t('cms:cms.students.detail.table.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      className: 'students-page__body__list__column students-page__body__list__column--min-w-180',
      render: text => {
        return (
          <span className="students-page__body__list__column-body students-page__body__list__column--min-w-180">
            {formatDay(dayFormatType.UKWITHHM, text) || '-'}
          </span>
        );
      },
      sorter: true,
    },
    {
      title: t('cms:cms.students.detail.table.action'),
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (text, record) => {
        return (
          record.bookingJourney &&
          record.bookingJourney !== bookingJourneyType.FULL_AUTOMATIC &&
          record.referenceId && (
            <div className="students-page__body__list__column-action">
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleView(record.bookingJourney, record.referenceId);
                }}
                className="students-page__column-action-link"
              >
                {t('cms:cms.students.detail.table.action.view')}
              </a>
            </div>
          )
        );
      },
    },
  ];

  return (
    <Panel
      emptyContent={t('cms:cms.students.detail.panel.leads.empty')}
      title={getTitle(t, listTotalCount)}
      headerShadow={listTotalCount === 0}
    >
      <Table
        className="table-list students-page__body__list"
        loading={loading}
        dataSource={leads}
        pagination={{
          current: pagination.pageNumber,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          hideOnSinglePage: false,
          total: listTotalCount,
        }}
        rowKey={record => record.id}
        columns={columns}
        onChange={handleTableChange}
      />
    </Panel>
  );
};

export default StudentLeads;
